import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { ApiResponse } from '../types';

/**
 * API 客户端配置
 * 基于 Axios 封装的 HTTP 请求客户端，支持多端独立 token 管理
 */

/** 请求超时时间（毫秒） */
const REQUEST_TIMEOUT = 300000;

/**
 * 图片生成接口专用超时（10 分钟 = 600000ms）
 *
 * 作用：仅 `/image-task/upload` 等生图接口使用本值，其余接口仍用 REQUEST_TIMEOUT(300000)，
 *       这样普通接口仍能快速失败反馈，不会被生图的长耗时拖成 5 分钟空等。
 *
 * 为什么是 600s：后端超时链必须逐层放大，本值位于最外层，必须 ≥ 其内所有层：
 *       TransferService 单张 300s < AISettings:TimeoutSeconds 360s < web.config requestTimeout 420s < ARR proxy 600s = 本值
 *
 * 修改注意（三处需一起改，否则会表现为 502 / CORS / 前端超时）：
 *   1) 本常量
 *   2) 生产 IIS 的 ARR proxy timeout（由 set-iis-timeout.ps1 设置）
 *   3) 后端 web.config 的 ANCM requestTimeout 与 appsettings.json 的 AISettings:TimeoutSeconds
 * 若只是想让普通接口更快失败，请改 REQUEST_TIMEOUT，不要动本值。
 */
export const GENERATION_TIMEOUT = 600000;

/** 客户端 token 配置（可由各端初始化时覆盖） */
const tokenConfig = {
  tokenKey: 'admin_token',
  refreshTokenKey: 'admin_refresh_token',
  refreshEndpoint: '/api/admin/auth/refresh-token',
};

/**
 * 配置 token 存储键名和刷新端点
 * 各端（admin-portal / user-portal）在入口处调用此函数进行初始化
 */
export function configureTokenKeys(options: {
  tokenKey: string;
  refreshTokenKey: string;
  refreshEndpoint: string;
}) {
  tokenConfig.tokenKey = options.tokenKey;
  tokenConfig.refreshTokenKey = options.refreshTokenKey;
  tokenConfig.refreshEndpoint = options.refreshEndpoint;
}

/**
 * 读取运行时后端地址
 * 优先从 window.__CONFIG__.API_BASE_URL 读取（生产环境由 config.js 注入），
 * 未配置时回退到相对路径（开发环境由 Vite proxy 转发）
 */
function getApiBaseUrl(): string {
  const runtimeBase = (window as any).__CONFIG__?.API_BASE_URL;
  if (runtimeBase) return runtimeBase + '/api';
  return '/api';
}

/**
 * 创建 axios 实例
 * 配置基础URL、超时时间和默认请求头
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 获取后端服务器基础URL（不含 /api 前缀）
 * 用于拼接图片等静态资源的相对路径
 * 开发环境返回 http://localhost:5000，生产环境返回空字符串
 */
export function getBackendBaseUrl(): string {
  return (window as any).__CONFIG__?.API_BASE_URL || '';
}

/**
 * 刷新令牌专用实例
 * 不能使用 apiClient：它挂了 401 拦截器，刷新失败时会再次触发刷新造成递归，
 * 而且 apiClient 的 baseURL 已含 /api，而 refreshEndpoint 本身也带 /api 前缀。
 * 这里用服务端根地址 + 完整端点路径，并用裸 axios 保证不经过任何拦截器。
 */
const refreshClient: AxiosInstance = axios.create({
  baseURL: getBackendBaseUrl(),
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * 请求拦截器 - 自动添加认证令牌
 * 从 localStorage 获取 token 并添加到请求头
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(tokenConfig.tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器 - 处理令牌刷新和全局错误
 * 401 错误时自动尝试刷新令牌，失败则跳转登录页
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>) => {
    const result = response.data;
    // 业务层面失败（isSuccess=false）时，提取错误信息并抛出
    if (result && result.isSuccess === false) {
      const businessError = new Error(result.error || result.message || '操作失败');
      (businessError as any).code = result.code;
      (businessError as any).isSuccess = false;
      return Promise.reject(businessError);
    }
    // 成功时返回 data 字段
    return result.data;
  },
  async (error) => {
    // Check timeout error
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const waitedSeconds = Math.round((error.config?.timeout ?? REQUEST_TIMEOUT) / 1000);
      return Promise.reject(new Error(`请求超时（已等待 ${waitedSeconds} 秒），请检查网络连接`));
    }

    const originalRequest = error.config;
    
    // 处理 401 未授权错误，尝试刷新令牌
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem(tokenConfig.refreshTokenKey);
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        // 调用刷新令牌接口（用专用实例，带正确 baseURL 且不经过拦截器）
        const response = await refreshClient.post(tokenConfig.refreshEndpoint, {
          refreshToken,
        });

        // 后端返回 Result<AuthResponse>：{ isSuccess, data: { accessToken, refreshToken, ... } }
        const payload = response.data;
        if (!payload?.isSuccess || !payload?.data?.accessToken) {
          throw new Error(payload?.error || '刷新令牌失败');
        }
        const { accessToken, refreshToken: newRefreshToken } = payload.data;
        localStorage.setItem(tokenConfig.tokenKey, accessToken);
        localStorage.setItem(tokenConfig.refreshTokenKey, newRefreshToken);
        
        // 使用新令牌重试原请求
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // 刷新失败，清除令牌并跳转登录页
        localStorage.removeItem(tokenConfig.tokenKey);
        localStorage.removeItem(tokenConfig.refreshTokenKey);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // ━━━ 429 限流保护拦截 ━━━
    // 后端 RateLimiter 触发时返回 429，这里统一处理为用户友好的提示
    // 不是系统出错，而是操作过于频繁触发了安全保护，告知用户稍后再试
    if (error.response?.status === 429) {
      const errorData = error.response?.data;
      const retryAfter = error.response?.headers?.['retry-after'];
      const waitHint = retryAfter ? `请等待约${retryAfter}秒后再试` : '请等待1分钟后再试';
      const rateLimitMessage = errorData?.message || `操作过于频繁，系统已触发限流保护。${waitHint}，感谢您的理解与耐心。`;
      const friendlyError = new Error(rateLimitMessage);
      (friendlyError as any).code = 429;
      (friendlyError as any).isRateLimited = true;  // 标记为限流错误，调用方可特殊处理
      (friendlyError as any).isSuccess = false;
      return Promise.reject(friendlyError);
    }

    // ━━━ 其他业务错误通用处理 ━━━
    // 提取后端返回的业务错误信息，包装成更友好的 Error 对象
    const errorData = error.response?.data;
    const businessMessage = errorData?.error || errorData?.message || error.message || '请求失败';
    const businessError = new Error(businessMessage);
    (businessError as any).code = errorData?.code || error.response?.status;
    (businessError as any).isSuccess = errorData?.isSuccess;
    return Promise.reject(businessError);
  }
);

export default apiClient;

/**
 * 通用 HTTP 请求方法封装
 * 提供类型安全的 GET、POST、PUT、DELETE、PATCH 方法
 */
export const http = {
  /**
   * GET 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param config 请求配置
   * @returns Promise<T>
   */
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config) as Promise<T>,
  
  /**
   * POST 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config) as Promise<T>,
  
  /**
   * PUT 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config) as Promise<T>,
  
  /**
   * DELETE 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param config 请求配置
   * @returns Promise<T>
   */
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config) as Promise<T>,
  
  /**
   * PATCH 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config) as Promise<T>,
};
